<template>
  <BaseDrawer
    :is-open="true"
    :shrink="
      step === 'success' ||
      step === 'methodSelect' ||
      step === 'configureSplit'
    "
    @close="onDrawerClose"
  >
    <div class="flex min-h-0 grow flex-col">
      <PaymentSelectMethod
        v-if="step === 'methodSelect'"
        :amount-gross="invoice.amountGross"
        @select-method="confirmTransactionMethod"
        @close="onDrawerClose"
      />
      <PaymentCardGratuity
        v-else-if="step === 'cardGratuity'"
        :amount-gross="invoice.amountGross"
        @submit="onConfirmCardGratuity"
        @cancel="emit('close')"
      />
      <PaymentCash
        v-else-if="step === 'cash'"
        :amount-gross="invoice.amountGross"
        @submit="onConfirmCash"
        @close="emit('close')"
      />
      <PaymentChange
        v-else-if="step === 'change'"
        :cash-tendered="totalCashTendered"
        :invoice-amount-gross="invoice.amountGross"
        @back="step = 'cash'"
        @submit="onConfirmChange"
      />
      <PaymentConfigureSplit
        v-else-if="step === 'configureSplit'"
        @submit="onConfirmSplitConfiguration"
        @cancel="onCancelConfigureSplit"
      />
      <PaymentSplit
        v-else-if="step === 'split'"
        :order-id="orderId"
        :split-configuration="splitConfiguration"
        @error="emit('closeAndClearOrder')"
        @success="onConfirmSplitSuccess"
        @cancel="onCancelSplit"
      />
      <PaymentSuccess
        v-else-if="step === 'success' && paidInvoice"
        :order-id="orderId"
        :invoice="paidInvoice"
        @done="emit('closeAndClearOrder')"
      />
    </div>

    <PaymentCashInsufficientAmountConfirmation
      ref="cashInsufficientAmountConfirmRef"
    />

    <SignatureConfirmation ref="signatureConfirmationRef" />

    <PaymentRetryDrawer ref="paymentRetryDrawerRef" />
  </BaseDrawer>
</template>

<script setup lang="ts">
import { IPayment } from "@/androidPlugins/IPayment"
import { IPrinter } from "@/androidPlugins/IPrinter"
import { createTransaction } from "@/services/createTransaction"
import type { OrderOpenStatus } from "@/services/sharedTypes/orders"
import { retrieveOrderReceipt } from "@/services/orders/retrieveOrderReceipt"
import * as Sentry from "@sentry/vue"
import type SignatureConfirmation from "./SignatureConfirmation.vue"
import type PaymentRetryDrawer from "./PaymentRetryDrawer.vue"
import { ILogger } from "@/androidPlugins/ILogger"
import type { CreateTransactionResponseInvoice } from "@/services/createTransaction"

const props = defineProps<{
  invoice: {
    id: string
    amountGross: number
  }
  orderId: string
  orderOpenStatus: OrderOpenStatus
}>()

const emit = defineEmits<{
  (event: "close"): void
  (event: "closeAndClearOrder"): void
}>()

const {
  isGratuityEnabled,
  isCardReceiptsEnabled,
  isCashEnabled,
  isSplitPaymentsEnabled,
  tenant,
  gratuityIsAvailable,
} = useTenant()
const { startLoading, stopLoading } =
  usePageBlockingLoading()
const { printingSupported } = useDevice()
const router = useRouter()
const route = useRoute()

const step = ref<
  | "methodSelect"
  | "cardGratuity"
  | "cash"
  | "change"
  | "configureSplit"
  | "split"
  | "retrySync"
  | "success"
  | null
>(null)
const cashInsufficientAmountConfirmRef = useTemplateRef(
  "cashInsufficientAmountConfirmRef"
)
const signatureConfirmationRef = useTemplateRef(
  "signatureConfirmationRef"
)
const splitConfiguration = ref<number | "manual">("manual")
const transactionMethod = ref<
  null | "cash" | "card" | "split" | "CNP"
>(null) // use to remember method between method confirmation and gratuity confirmation
const totalCashTendered = ref(0) //  prop for PaymentChange component
const paidInvoice = ref<CreateTransactionResponseInvoice>() // prop for PaymentSuccess

onMounted(async () => {
  if (props.orderOpenStatus !== "open") {
    step.value = "split"
  } else if (
    !isCashEnabled.value &&
    !isSplitPaymentsEnabled.value
  ) {
    confirmTransactionMethod("card")
  } else {
    step.value = "methodSelect"
  }
})

const onDrawerClose = () => {
  if (
    step.value === "cardGratuity" ||
    step.value === "cash" ||
    step.value === "methodSelect" ||
    step.value === "configureSplit"
  ) {
    emit("close")
  } else if (step.value === "success") {
    emit("closeAndClearOrder")
  }
}

const confirmTransactionMethod = async (
  value: "cash" | "card" | "split" | "CNP"
) => {
  ILogger.logToDetailLog({
    message: "PaymentDrawer: Click Pay",
  })

  transactionMethod.value = value

  if (value === "cash") {
    step.value = "cash"
  } else if (
    (value === "card" || value === "CNP") &&
    isGratuityEnabled.value
  ) {
    step.value = "cardGratuity"
  } else if (value === "card" || value === "CNP") {
    await onConfirmCard(
      props.invoice.amountGross,
      0,
      value === "CNP"
    )
  } else if (
    value === "split" &&
    props.orderOpenStatus !== "partially_paid"
  ) {
    step.value = "configureSplit"
  } else {
    step.value = "split"
  }
}

const onConfirmCardGratuity = async (value: number) => {
  await onConfirmCard(
    props.invoice.amountGross,
    value,
    transactionMethod.value === "CNP"
  )
}

const onConfirmCard = async (
  amountTrans: number,
  amountGratuity: number,
  cardholderNotPresent: boolean
) => {
  const devicePaymentData = await makeDeviceCardPayment(
    amountTrans,
    amountGratuity,
    cardholderNotPresent
  )

  if (devicePaymentData === null) {
    emit("close")
    return
  }

  startLoading()
  try {
    const { invoice, ...cardTransaction } =
      await syncTransaction(props.invoice.id, {
        data: devicePaymentData,
        source: "pos_card",
      })

    const showSignatureConfirmation =
      "signatureStatus" in cardTransaction &&
      cardTransaction.signatureStatus ===
        "AWAITING APPROVAL"

    if (
      showSignatureConfirmation &&
      signatureConfirmationRef.value
    ) {
      await maybePrintMerchantReceipt(true) // Contains signature line

      const { isCanceled: signatureIsRejected } =
        await signatureConfirmationRef.value.reveal({
          transactionId: cardTransaction.id,
          transactionUti: cardTransaction.uti ?? "",
          transactionAmount:
            cardTransaction.amountTransactedTotal,
        })

      if (signatureIsRejected) {
        router.push({
          name: "postPaymentOrderDetail",
          params: { orderId: props.orderId },
          query: { previous_page: route.path },
        })

        return
      }
    }

    switch (cardTransaction.status) {
      case "cancelled": {
        emit("close")

        break
      }
      case "failed": {
        await maybePrintMerchantReceipt()

        router.push({
          name: "postPaymentOrderDetail",
          params: { orderId: props.orderId },
          query: { previous_page: route.path },
        })

        break
      }
      case "successful": {
        await onSuccess(invoice, showSignatureConfirmation) // If we printed a merchant receipt with signature line no need to print another merchant receipt

        break
      }
      default: {
        emit("closeAndClearOrder")
      }
    }
  } catch (error) {
    Sentry.captureException(error)

    toast.error("Failed to create card transaction")
  } finally {
    stopLoading()
  }
}

const paymentRetryDrawerRef = useTemplateRef(
  "paymentRetryDrawerRef"
)
const syncTransaction = async (
  ...arguments_: Parameters<typeof createTransaction>
): ReturnType<typeof createTransaction> => {
  startLoading()
  try {
    const response = await createTransaction(...arguments_)
    stopLoading()
    return response
  } catch (error) {
    stopLoading()
    Sentry.captureException(error)
    if (paymentRetryDrawerRef.value === null) {
      throw new Error("Payment Retry Drawer not loaded")
    }

    const { data, isCanceled } =
      await paymentRetryDrawerRef.value.reveal(arguments_)

    if (isCanceled || data === undefined) {
      throw new Error("Payment sync ignored by user")
    }

    return data
  }
}

const makeDeviceCardPayment = async (
  amountTrans: number,
  amountGratuity: number,
  cardholderNotPresent: boolean
) => {
  startLoading()
  try {
    const devicePaymentResponse =
      await IPayment.makeDevicePayment({
        amountTrans,
        amountGratuity,
        disablePrinting:
          !isCardReceiptsEnabled.value ||
          !printingSupported.value,
        gratuityIsAvailable: gratuityIsAvailable.value,
        cardholderNotPresent: cardholderNotPresent,
      })

    return devicePaymentResponse.data
  } catch (error) {
    toast.error("Payment application failed")
    Sentry.captureException(error)
    return null
  } finally {
    stopLoading()
  }
}

const onConfirmCash = async (cashTendered: number) => {
  totalCashTendered.value = cashTendered
  if (cashTendered < props.invoice.amountGross) {
    await onInsufficientCashTendered(cashTendered)
  }

  if (cashTendered == props.invoice.amountGross) {
    await onExactCashTendered(cashTendered)
  }

  if (cashTendered > props.invoice.amountGross) {
    await onExcessiveCashTendered(cashTendered)
  }
}

const onExcessiveCashTendered = async (
  cashTendered: number
) => {
  if (isGratuityEnabled.value) {
    step.value = "change"
  } else {
    await onConfirmChange(
      cashTendered,
      cashTendered - props.invoice.amountGross
    )
  }
}

const onInsufficientCashTendered = async (
  cashTendered: number
) => {
  if (cashInsufficientAmountConfirmRef.value === null)
    return

  const { data, isCanceled } =
    await cashInsufficientAmountConfirmRef.value.reveal()

  if (isCanceled) return

  if (data?.payRestWithCard) {
    startLoading()
    try {
      const devicePaymentData = await makeDeviceCardPayment(
        props.invoice.amountGross - cashTendered,
        0,
        false
      )

      if (devicePaymentData === null) {
        return
      }

      const cardTransaction = await syncTransaction(
        props.invoice.id,
        {
          data: devicePaymentData,
          source: "pos_card",
        }
      )

      if (cardTransaction.status !== "successful") {
        if (cardTransaction.status === "failed") {
          await maybePrintMerchantReceipt()
        }
        // We don't want to close drawer here as they can retry card transaction
        toast.error("Card transaction was unsuccessful")
        return
      }

      const { invoice } = await syncTransaction(
        props.invoice.id,
        {
          source: "pos_cash",
          data: {
            cashTendered,
            amountGratuity: 0,
          },
        }
      )

      await onSuccess(invoice)
    } catch (error) {
      // Important to close whole order drawer as if faliure is on cash transaction money may have already been taken from card
      toast.error("Something went wrong")
      Sentry.captureException(error)
      emit("closeAndClearOrder")
    } finally {
      stopLoading()
    }
  } else {
    startLoading()
    try {
      const { invoice } = await syncTransaction(
        props.invoice.id,
        {
          source: "pos_cash",
          data: {
            cashTendered,
            amountGratuity: 0,
          },
          forceInvoicePaid: true,
        }
      )

      await onSuccess(invoice)
    } catch {
      // No need to close drawer as they can just retry
      toast.error("Something went wrong")
    } finally {
      stopLoading()
    }
  }
}

const onExactCashTendered = async (
  cashTendered: number
) => {
  startLoading()
  try {
    const { invoice } = await syncTransaction(
      props.invoice.id,
      {
        source: "pos_cash",
        data: {
          cashTendered,
          amountGratuity: 0,
        },
      }
    )
    await onSuccess(invoice)
  } catch {
    // No need to close drawer as they can just retry
    toast.error("Something went wrong")
  } finally {
    stopLoading()
  }
}

const onConfirmChange = async (
  cashTendered: number,
  changeGiven: number
) => {
  const amountTransactedTotal = Math.max(
    0,
    cashTendered - changeGiven
  )
  const amountTransactedGross = Math.min(
    props.invoice.amountGross,
    amountTransactedTotal
  )
  const amountTransactedGratuity = Math.max(
    0,
    amountTransactedTotal - amountTransactedGross
  )

  startLoading()
  try {
    const { invoice } = await syncTransaction(
      props.invoice.id,
      {
        source: "pos_cash",
        data: {
          cashTendered,
          amountGratuity: amountTransactedGratuity,
        },
      }
    )

    await onSuccess(invoice)
  } catch {
    // No need to close drawer as they can just retry

    toast.error("Something went wrong")
  } finally {
    stopLoading()
  }
}

const onConfirmSplitConfiguration = (
  value: number | "manual"
) => {
  splitConfiguration.value = value
  step.value = "split"
}

const onCancelConfigureSplit = () => {
  step.value = "methodSelect"
}
const onCancelSplit = () => {
  step.value = "configureSplit"
}

const onConfirmSplitSuccess = async (
  invoice: CreateTransactionResponseInvoice
) => {
  paidInvoice.value = invoice
  step.value = "success"
}

const onSuccess = async (
  invoice: CreateTransactionResponseInvoice,
  skipMerchantReceipt?: boolean
) => {
  !skipMerchantReceipt &&
    (await maybePrintMerchantReceipt())

  paidInvoice.value = invoice
  step.value = "success"
}

const maybePrintMerchantReceipt = async (
  forcePrint?: boolean
) => {
  if (
    (tenant.isAutomaticMerchantReceiptEnabled &&
      printingSupported.value) ||
    forcePrint
  ) {
    startLoading()
    try {
      const { receipt } = await retrieveOrderReceipt(
        props.orderId,
        {
          isMerchant: true,
        }
      )

      await IPrinter.printStr({ str: receipt })
    } catch {
      toast.error("Failed to generate receipt")
    } finally {
      stopLoading()
    }
  }
}
</script>
